import { InventoryItem } from '../redux/inventorySlice';
import { RecipeIngredient } from '../types/recipe';
import { shelfMatcher } from './pantryMatch';

export interface InventoryMatchResult {
  matched: boolean;
  inventoryItemId?: string;
  inventoryItemName?: string;
  inventoryQuantity?: number;
}

/**
 * The handle a caller looks a result up by — and, in PantryMatchStrip and
 * IngredientReviewSheet, the short label shown next to it. Kept on `name` for
 * exactly that reason: `original` is the whole recipe line and would not fit a
 * pill.
 *
 * Every caller must key with THIS, never with its own inlined copy of the
 * expression; four of them had one, and the drift between them is #601.
 */
export const ingredientMatchKey = (ingredient: RecipeIngredient): string =>
  ingredient.name || ingredient.original;

/**
 * The text a match is computed FROM — deliberately the opposite precedence to
 * the key above (#601).
 *
 * The display and the pantry write path both reduce `original || name`, so the
 * matcher has to as well or the string that gets stored and the string that
 * gets matched come from two different fields and agree only by luck. `name` is
 * the scraper's parsed field and is regularly the more damaged of the two:
 * "1 pound thin, round rice noodles" arrives with `name` = "thin", and
 * "½ c. à thé (2,5 ml) de piment de la Jamaïque" with `name` = "½ c. à thé (2".
 * Neither can be reduced to the food the user put away.
 */
export const ingredientMatchText = (ingredient: RecipeIngredient): string =>
  ingredient.original || ingredient.name;

/**
 * Match recipe ingredients against the user's inventory.
 * Returns a map from `ingredientMatchKey` to match result.
 *
 * A match is the recipe screen's check mark — coverage `have` through
 * `pantryMatch.ts` — so a planner card, the review sheet and the deduction
 * after cooking say what the recipe screen says. They used to run their own
 * exact → fuzzy → containment test on `normalizeIngredientName`, whose FR→EN
 * table only reads whole strings: "citrons" never met "2 lemons, juiced".
 * `probably` (limes for lime juice) is not a match here; the recipe screen
 * asks about it rather than deciding.
 */
export function getInventoryMatchesForRecipe(
  ingredients: RecipeIngredient[],
  inventory: InventoryItem[]
): Map<string, InventoryMatchResult> {
  const result = new Map<string, InventoryMatchResult>();

  if (ingredients.length === 0) return result;

  const shelf = shelfMatcher(inventory);

  for (const ingredient of ingredients) {
    const match = shelf.matchLine(ingredientMatchText(ingredient));
    const matchedItem = match.state === 'have' ? match.items[0] : undefined;
    result.set(
      ingredientMatchKey(ingredient),
      matchedItem
        ? {
            matched: true,
            inventoryItemId: matchedItem.id,
            inventoryItemName: matchedItem.name,
            inventoryQuantity: matchedItem.quantity ?? 1,
          }
        : { matched: false }
    );
  }

  return result;
}
